/**
 * @created     2026-02-27
 * @updated     2026-02-27
 * @description Sends chat messages to the backend POST /api/chat endpoint and
 *              returns the bot response as an Observable.
 *              Depends on: HttpClient, BotResponse model
 *              Session ID is persisted in sessionStorage across page reloads
 *              but cleared when the tab closes.
 */
import { Injectable, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { BotResponse, ChatRequest, DebugCaseExport } from '../models/bot-response.model';
import { I18nService } from './i18n.service';

interface ApiResponse {
  success: boolean;
  data: BotResponse;
}

// HttpClient preserves this body in HttpErrorResponse.error on non-2xx responses.
export interface ApiErrorEnvelope {
  success?: false;
  code?: string;
  message?: string;
  error?: string | { code?: string; message?: string };
  requestId?: string;
  meta?: { requestId?: string };
}

interface NewSessionResponse {
  success: boolean;
  data: {
    session_id: string;
    aktennummer: string;
    unterakte: string;
  };
}

export interface SessionReportMetadata {
  url?: string;
  path?: string;
  language?: string;
  user_agent?: string;
  tester_name?: string;
  chat_history?: string;
}

export interface SessionReportResponse {
  success: boolean;
  data: {
    report_id?: string;
    mail_sent: boolean;
  };
}

export interface PayPalOrderResponse {
  success: boolean;
  data: {
    order_id: string;
    status: string;
    approval_url: string;
  };
}

export interface PayPalCaptureResponse {
  success: boolean;
  data: {
    order_id: string;
    status: string;
    capture_id?: string;
    completion_message?: string;
    debugExport?: DebugCaseExport;
  };
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly http = inject(HttpClient);
  private readonly i18n = inject(I18nService);
  private readonly apiUrl = '/api/chat';
  private readonly newSessionUrl = '/api/auth/session/new';
  private readonly sessionReportUrl = '/api/session-report';
  private readonly paypalOrderUrl = '/api/paypal/order';
  private readonly paypalCaptureUrl = '/api/paypal/capture';
  private readonly quickActionRequestsSubject = new Subject<string>();
  private readonly modalTurnIds = new Map<string, string>();
  private sessionId: string | null = null;
  readonly quickActionRequests$ = this.quickActionRequestsSubject.asObservable();

  constructor() {
    this.sessionId = sessionStorage.getItem('chat_session_id');
  }

  /**
   * Sends a user message to the bot and returns the bot's response.
   *
   * @param text User input text
   * @returns Observable of BotResponse
   */
  sendMessage(text: string): Observable<BotResponse> {
    const body: ChatRequest = {
      message: text,
      language: this.i18n.language(),
      turn_id: this.createTurnId(),
    };
    const sentSessionId = this.sessionId;
    if (sentSessionId) {
      body.session_id = sentSessionId;
    }

    if (isDevMode()) {
      console.log('📤 [CHAT] Nachricht gesendet:', text);
    }

    return this.http.post<ApiResponse>(this.apiUrl, body).pipe(
      // A reply sent under another (e.g. previous login's) session must never mutate the active one.
      filter(() => this.sessionId === sentSessionId),
      switchMap((res) => from(this.handleResponseData(res.data)).pipe(
        map((data) => {
          if (isDevMode()) {
            console.log('📥 [CHAT] Antwort:', data.response, '| State:', data.state);
          }
          return data;
        })
      ))
    );
  }

  private async handleResponseData(data: BotResponse): Promise<BotResponse> {
    if (data.session_id && data.session_id !== this.sessionId) {
      this.sessionId = data.session_id;
      sessionStorage.setItem('chat_session_id', this.sessionId);
    }

    if (data.language && data.language !== this.i18n.language() && data.language_changed === true) {
      await this.i18n.setLanguage(data.language, 'backend');
    }

    return data;
  }

  /**
   * Sends a quick-action request to the chat widget.
   * The widget handles queueing and optional greeting bootstrap.
   */
  requestQuickAction(message: string): void {
    this.quickActionRequestsSubject.next(message);
  }

  /**
   * Creates a fresh authenticated chat session for the current case.
   */
  createNewSession(): Observable<string> {
    const currentSessionId = this.sessionId;
    return this.http.post<NewSessionResponse>(this.newSessionUrl, {
      session_id: currentSessionId,
      language: this.i18n.language(),
    }).pipe(
      map((res) => {
        const sessionId = res.data.session_id;
        this.setSessionId(sessionId);
        sessionStorage.setItem('cr_session_id', JSON.stringify({
          ...this.storedCaseSessionContext(),
          sessionId,
          ts: Date.now(),
          aktennummer: res.data.aktennummer,
          unterakte: res.data.unterakte,
        }));
        return sessionId;
      })
    );
  }

  /**
   * Notifies the backend that a modal was completed (e.g. payment contract confirmed).
   * Sends __MODAL_COMPLETE__:modalId[:nonce] as the message text.
   *
   * @param modalId Identifier of the completed modal (e.g. 'payment_contract')
   * @param nonce One-time nonce issued with the modal action
   * @param modalPayload Optional modal form payload for backend validation
   * @returns Observable of BotResponse
   */
  sendModalComplete(modalId: string, nonce?: string, modalPayload?: Record<string, any>): Observable<BotResponse> {
    const text = nonce
      ? `__MODAL_COMPLETE__:${modalId}:${nonce}`
      : `__MODAL_COMPLETE__:${modalId}`;
    const turnId = nonce ? this.getModalTurnId(modalId, nonce, modalPayload) : this.createTurnId();
    const body: ChatRequest = {
      message: text,
      language: this.i18n.language(),
      turn_id: turnId,
    };
    const sentSessionId = this.sessionId;
    if (sentSessionId) {
      body.session_id = sentSessionId;
    }
    if (modalPayload) {
      body.modal_payload = modalPayload;
    }

    if (isDevMode()) {
      console.log('📤 [CHAT] Modal-Complete gesendet:', modalId);
    }

    return this.http.post<ApiResponse>(this.apiUrl, body).pipe(
      // A reply sent under another (e.g. previous login's) session must never mutate the active one.
      filter(() => this.sessionId === sentSessionId),
      switchMap((res) => from(this.handleResponseData(res.data)).pipe(
        map((data) => {
          if (isDevMode()) {
            console.log('📥 [CHAT] Modal-Complete Antwort:', data.response, '| State:', data.state);
          }
          return data;
        })
      ))
    );
  }

  reportSession(message: string, client: SessionReportMetadata): Observable<SessionReportResponse> {
    const sessionId = this.getSessionId();
    return this.http.post<SessionReportResponse>(this.sessionReportUrl, {
      session_id: sessionId,
      message,
      client,
    });
  }

  createPaypalOrder(context?: 'quick_action_full_payment'): Observable<PayPalOrderResponse> {
    const body: { session_id: string | null; context?: 'quick_action_full_payment' } = {
      session_id: this.getSessionId(),
    };
    if (context) {
      body.context = context;
    }

    return this.http.post<PayPalOrderResponse>(this.paypalOrderUrl, body);
  }

  capturePaypalOrder(orderId: string): Observable<PayPalCaptureResponse> {
    return this.http.post<PayPalCaptureResponse>(this.paypalCaptureUrl, {
      session_id: this.getSessionId(),
      order_id: orderId,
    });
  }

  /**
   * Returns the current session ID, or null if no session has started yet.
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Sets the session ID explicitly (used after successful login).
   */
  setSessionId(sessionId: string): void {
    if (this.sessionId !== sessionId) {
      this.modalTurnIds.clear();
    }

    this.sessionId = sessionId;
    sessionStorage.setItem('chat_session_id', sessionId);
    if (isDevMode()) {
      console.log('🔐 [CHAT] Session gesetzt:', sessionId);
    }
  }

  /**
   * Clears the current session ID, forcing a new session on the next message.
   */
  resetSession(): void {
    this.sessionId = null;
    this.modalTurnIds.clear();
    sessionStorage.removeItem('chat_session_id');
    if (isDevMode()) {
      console.log('🔄 [CHAT] Session zurückgesetzt');
    }
  }

  private getModalTurnId(modalId: string, nonce: string, modalPayload?: Record<string, any>): string {
    const key = `${modalId}:${nonce}:${this.canonicalPayload(modalPayload)}`;
    const existing = this.modalTurnIds.get(key);
    if (existing) {
      return existing;
    }

    const turnId = this.createTurnId();
    this.modalTurnIds.set(key, turnId);
    return turnId;
  }

  private canonicalPayload(payload?: Record<string, any>): string {
    return JSON.stringify(payload ?? null, (_key, value) => {
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return value;
      }

      return Object.keys(value).sort().reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = value[key];
        return sorted;
      }, {});
    });
  }

  private createTurnId(): string {
    if (globalThis.crypto?.randomUUID) {
      return `turn:${globalThis.crypto.randomUUID()}`;
    }

    return `turn:${Date.now().toString(36)}:${Math.random().toString(36).slice(2)}`;
  }

  private storedCaseSessionContext(): Record<string, unknown> {
    const stored = sessionStorage.getItem('cr_session_id');
    if (!stored) {
      return {};
    }

    try {
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

}
